import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Box, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import BaseBox from '../_Common/BaseBox';
import {
  BodyBox,
  RowBox,
  ValueBox,
  TextWin,
  TextLoss,
} from '../_Common/CommonComponents';
import NoData from '../_Common/NoData';
import CustomCircularProgress from '../_Common/CustomCircularProgress';
import { setPercentColor, hourToTimezone } from '../../../../../utils/functions';
import { ApplicationState } from '../../../../../store';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  titles: {
    width: '60px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: 600,
    marginLeft: '7px',
  },
  label: {
    background: 'transparent !important',
    fontSize: '14px',
    color: '#ffffffdd',
    fontWeight: 500,
  },
  otc: {
    position: 'absolute' as any,
    top: '-8px',
    right: '0px',
    fontSize: '9px',
  },
  divOtc: {
    position: 'relative' as any,
    background: 'transparent !important',
    '& .MuiTypography-root': {
      fontSize: '14px',
      color: '#ffffffdd',
    },
  },
  hours: {
    width: 'max-content',
    fontSize: '14px',
    color: '#ffffffdd',
  },
  navButton: {
    color: '#ffffffbb',
    padding: '2px',
    '&:hover': {
      color: '#ffffff',
    },
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
});

const TABS = [
  { key: 'assets', title: 'Ativos com mais acertos' },
  { key: 'hours', title: 'Melhores horarios' },
  { key: 'days', title: 'Dias da semana' },
];

interface ParsedAsset {
  asset: string;
  percentOfWin: number;
  qtyWin: number;
  qtyLoss: number;
  color: string;
}

interface ParsedHour {
  hour: string;
  percentOfWin: number;
  qtyWin: number;
  qtyLoss: number;
  color: string;
}

interface ParsedDay {
  dayOfWeek: string;
  qtyWin: number;
  qtyLoss: number;
  percentOfWin: number;
  color: string;
}

function correctDayOfWeek(day: number): string {
  switch (day) {
    case 1: return 'Domingo';
    case 2: return 'Segunda';
    case 3: return 'Terca';
    case 4: return 'Quarta';
    case 5: return 'Quinta';
    case 6: return 'Sexta';
    case 7: return 'Sabado';
    default: return 'Erro';
  }
}

export default function StatsCarousel() {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const [parsedAssets, setParsedAssets] = useState<ParsedAsset[]>([]);
  const [parsedHours, setParsedHours] = useState<ParsedHour[]>([]);
  const [parsedDays, setParsedDays] = useState<ParsedDay[]>([]);

  const { loading, boxes } = useSelector(
    (state: ApplicationState) => state.boxes
  );

  useEffect(() => {
    if (boxes.AssetsMostSucessful) {
      const parsed = boxes.AssetsMostSucessful.map((item: any) => ({
        ...item,
        color: setPercentColor(item.percentOfWin),
      }));
      setParsedAssets(parsed.slice(0, 5));
    }
  }, [boxes.AssetsMostSucessful]);

  useEffect(() => {
    if (boxes.BestHours) {
      const parsed = boxes.BestHours.map((item: any) => ({
        ...item,
        hour: `${`0${hourToTimezone(item.hour)}`.slice(-2)} - ${`0${hourToTimezone(item.hour) * 1 + 1}`.slice(-2)}h`,
        color: setPercentColor(item.percentOfWin),
      }));
      setParsedHours(parsed.slice(0, 5));
    }
  }, [boxes.BestHours]);

  useEffect(() => {
    if (boxes.BestDaysOfWeek) {
      const parsed = boxes.BestDaysOfWeek.map((item: any) => ({
        ...item,
        dayOfWeek: correctDayOfWeek(item.dayOfWeek),
        color: setPercentColor(item.percentOfWin),
      }));
      setParsedDays(parsed);
    }
  }, [boxes.BestDaysOfWeek]);

  const prev = () => setTabIndex(i => (i - 1 + TABS.length) % TABS.length);
  const next = () => setTabIndex(i => (i + 1) % TABS.length);

  function correctOTC(asset: string) {
    const parts = asset.split(' ');
    if (parts.length === 1) {
      return <Typography variant="body2">{parts[0]}</Typography>;
    }
    return (
      <Typography variant="body2">
        {parts[0]}
        <span className={classes.otc}>{parts[1]}</span>
      </Typography>
    );
  }

  const currentTab = TABS[tabIndex];

  function hasData() {
    if (currentTab.key === 'assets') return parsedAssets.length > 0;
    if (currentTab.key === 'hours') return parsedHours.length > 0;
    if (currentTab.key === 'days') return parsedDays.length > 0;
    return false;
  }

  const navAction = (
    <div className={classes.navContainer}>
      <IconButton className={classes.navButton} onClick={prev} size="small">
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton className={classes.navButton} onClick={next} size="small">
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </div>
  );

  function renderContent() {
    if (currentTab.key === 'assets') {
      return (
        <BodyBox>
          <Box>
            <RowBox>
              <TextWin className={classes.titles} variant="body2">win</TextWin>
              <TextLoss className={classes.titles} variant="body2">loss</TextLoss>
              <Typography className={classes.titles} variant="body2">%</Typography>
            </RowBox>
            {parsedAssets.map(item => (
              <RowBox key={item.asset}>
                <Box className={classes.divOtc}>{correctOTC(item.asset)}</Box>
                <ValueBox><Typography variant="body2">{item.qtyWin}</Typography></ValueBox>
                <ValueBox><Typography variant="body2">{item.qtyLoss}</Typography></ValueBox>
                <ValueBox>
                  <Typography style={{ color: item.color }} variant="body2">{item.percentOfWin}%</Typography>
                </ValueBox>
              </RowBox>
            ))}
          </Box>
        </BodyBox>
      );
    }

    if (currentTab.key === 'hours') {
      return (
        <BodyBox>
          <Box>
            <RowBox>
              <TextWin className={classes.titles} variant="body2">win</TextWin>
              <TextLoss className={classes.titles} variant="body2">loss</TextLoss>
              <Typography className={classes.titles} variant="body2">%</Typography>
            </RowBox>
            {parsedHours.map(item => (
              <RowBox key={item.hour}>
                <Box style={{ background: 'transparent' }}>
                  <Typography className={classes.hours} variant="body2">{item.hour}</Typography>
                </Box>
                <ValueBox><Typography variant="body2">{item.qtyWin}</Typography></ValueBox>
                <ValueBox><Typography variant="body2">{item.qtyLoss}</Typography></ValueBox>
                <ValueBox>
                  <Typography variant="body2" style={{ color: item.color }}>{item.percentOfWin}%</Typography>
                </ValueBox>
              </RowBox>
            ))}
          </Box>
        </BodyBox>
      );
    }

    if (currentTab.key === 'days') {
      return (
        <BodyBox>
          <Box>
            <RowBox>
              <TextWin className={classes.titles} variant="body2">win</TextWin>
              <TextLoss className={classes.titles} variant="body2">loss</TextLoss>
              <Typography className={classes.titles} variant="body2">%</Typography>
            </RowBox>
            {parsedDays.map(item => (
              <RowBox key={item.dayOfWeek}>
                <Box className={classes.label}>{item.dayOfWeek}</Box>
                <ValueBox><Typography variant="body2">{item.qtyWin}</Typography></ValueBox>
                <ValueBox><Typography variant="body2">{item.qtyLoss}</Typography></ValueBox>
                <ValueBox>
                  <Typography style={{ color: item.color }} variant="body2">{item.percentOfWin}%</Typography>
                </ValueBox>
              </RowBox>
            ))}
          </Box>
        </BodyBox>
      );
    }

    return null;
  }

  return (
    <BaseBox title={currentTab.title} action={navAction}>
      {!(loading && !hasData()) && hasData() && renderContent()}
      {loading && !hasData() && <CustomCircularProgress />}
      {!loading && !hasData() && <NoData />}
    </BaseBox>
  );
}
